import { useEffect } from 'react';

/**
 * Per-page titles, descriptions and social tags.
 *
 * ## The bug this exists to fix
 *
 * `index.html` carried one `<title>` for every route. Google indexed `/login`, found no title that
 * described that page, and wrote one itself by lifting words off it. The result in search was:
 *
 *     back - Infrastructure Automation Platform
 *
 * "back" out of "Welcome back". A search engine will invent a title when the page does not supply a
 * useful one, and the invented title is what a customer sees before they see anything else.
 *
 * ## This half is for the person; the build handles the rest
 *
 * What this hook does is keep the tab title and the head correct while somebody clicks around the
 * app, which is a rendering concern and belongs in a component.
 *
 * What it cannot do is decide what the server sends. These tags are written by JavaScript after the
 * app boots, so anything that reads the raw response — every social preview scraper, and any
 * crawler that has not got round to rendering the page yet — sees only the shell. That is now
 * handled at build time instead: `scripts/prerender.mjs` writes a real HTML file per route with
 * these same values already in it, from the same source this hook reads. The two agree because they
 * are generated from one list, in `@/seo/pages` and `@/seo/routes`.
 *
 * Structured data is pre-render only, and deliberately: it exists for parsers, all of which fetch a
 * URL directly rather than navigating a single-page app, so there is nothing for a second
 * client-side copy to be right about. What this hook does do is clear a pre-rendered block once the
 * visitor has navigated away from the page it described.
 */

export const SITE_NAME = 'Techie Gain';

/** Where canonical URLs point. */
const CANONICAL_ORIGIN = 'https://techiegain.com';

/**
 * The social preview image, absolute because scrapers do not resolve relative paths.
 *
 * Exported with its size and description so the pre-render writes exactly the same tags.
 */
export const SOCIAL_IMAGE = {
  url: `${CANONICAL_ORIGIN}/og-image.png`,
  width: 1200,
  height: 630,
  alt: 'Techie Gain: automation, monitoring and backups for Linux servers',
} as const;

/** The id given to the pre-rendered structured-data block, so it can be found and removed. */
export const JSON_LD_ELEMENT_ID = 'seo-structured-data';

export interface PageMeta {
  /**
   * The page's own title, without the site name.
   *
   * `Login` becomes `Login · Techie Gain`. `null` for the home page, whose title is the positioning
   * line rather than a section name.
   */
  title: string | null;
  /** The `<title>` when it should say more than the page's own name. See `SitePage.seoTitle`. */
  seoTitle?: string | undefined;
  /** The search-result snippet. Aim for 140-160 characters and write it to be read. */
  description: string;
  /** Anything behind a login, and anything that would be a duplicate or a dead end in search. */
  noindex?: boolean;
  /**
   * The preview image when the page has one of its own, as a blog post does, rather than the
   * site's. Absolute, because scrapers do not resolve relative paths.
   */
  image?: { url: string; width: number; height: number; alt: string } | undefined;
  /** `article` for a post or a guide, which is a document about one subject. */
  ogType?: 'website' | 'article' | undefined;
}

/**
 * The complete `<title>` for a page.
 *
 * Exported because the pre-render composes titles for the same pages, and a second implementation
 * of "how a title is put together" is a second thing to keep in step.
 */
export function fullTitle(title: string | null): string {
  return title === null ? `${SITE_NAME} · Server Automation, Monitoring and Backups` : `${title} · ${SITE_NAME}`;
}

/**
 * The canonical URL for a path.
 *
 * The trailing slash is dropped because `/pricing` and `/pricing/` serve the same file — nginx
 * resolves both to the same pre-rendered directory index — and two addresses for one page is the
 * thing a canonical tag exists to settle.
 */
export function canonicalUrl(pathname: string): string {
  const trimmed = pathname.length > 1 ? pathname.replace(/\/+$/, '') : '/';

  return `${CANONICAL_ORIGIN}${trimmed}`;
}

function upsert(selector: string, create: () => HTMLElement): HTMLElement {
  const existing = document.head.querySelector<HTMLElement>(selector);

  if (existing !== null) {
    return existing;
  }

  const element = create();
  document.head.appendChild(element);

  return element;
}

function setMeta(attribute: 'name' | 'property', key: string, value: string): void {
  const element = upsert(`meta[${attribute}="${key}"]`, () => {
    const meta = document.createElement('meta');
    meta.setAttribute(attribute, key);

    return meta;
  });

  element.setAttribute('content', value);
}

/**
 * Drops the pre-rendered structured data once it no longer describes the page being looked at.
 *
 * The block is stamped with the path it was written for. A visitor who lands on an article and then
 * clicks through to pricing would otherwise carry the article's markup with them — harmless for
 * search engines, which never navigate that way, and wrong for anything reading the live document.
 */
function discardStaleStructuredData(pathname: string): void {
  const script = document.getElementById(JSON_LD_ELEMENT_ID);

  if (script !== null && script.getAttribute('data-path') !== pathname) {
    script.remove();
  }
}

/**
 * Writes the document head for the current page.
 *
 * Deliberately no cleanup of the title on unmount. Restoring a default between routes produces a
 * visible flicker in the tab title, and every route that matters calls this — a route that does not
 * simply keeps the previous page's tags until the next one runs, which is a better failure than a
 * flash of the site name on every navigation.
 */
export function usePageMeta({ title, seoTitle, description, noindex = false, image, ogType }: PageMeta): void {
  // Primitives, so the effect depends on values and not on a fresh object every render.
  const previewUrl = image?.url ?? SOCIAL_IMAGE.url;
  const previewWidth = image?.width ?? SOCIAL_IMAGE.width;
  const previewHeight = image?.height ?? SOCIAL_IMAGE.height;
  const previewAlt = image?.alt ?? SOCIAL_IMAGE.alt;
  const type = ogType ?? 'website';

  useEffect(() => {
    const full = fullTitle(seoTitle ?? title);

    document.title = full;

    setMeta('name', 'description', description);
    setMeta('name', 'robots', noindex ? 'noindex, nofollow' : 'index, follow');

    const canonical = canonicalUrl(window.location.pathname);

    discardStaleStructuredData(window.location.pathname);

    const link = upsert('link[rel="canonical"]', () => {
      const created = document.createElement('link');
      created.setAttribute('rel', 'canonical');

      return created;
    });
    link.setAttribute('href', canonical);

    setMeta('property', 'og:site_name', SITE_NAME);
    setMeta('property', 'og:locale', 'en_US');
    setMeta('property', 'og:type', type);
    setMeta('property', 'og:title', full);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', canonical);
    setMeta('property', 'og:image', previewUrl);
    setMeta('property', 'og:image:width', String(previewWidth));
    setMeta('property', 'og:image:height', String(previewHeight));
    setMeta('property', 'og:image:alt', previewAlt);

    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', full);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', previewUrl);
    setMeta('name', 'twitter:image:alt', previewAlt);
  }, [title, seoTitle, description, noindex, previewUrl, previewWidth, previewHeight, previewAlt, type]);
}
